import { Component, Input, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { AuthState, IdentityAuthService } from "../../_services/identity-auth.service";
import { AccessTokenResponse, ApiException, ChangeExpiredPasswordRequest, IdentityClient } from "../../api_identity/api";
import { UserService } from "../../_services/user.service";
import { first } from "rxjs";

@Component({
  selector: 'app-signin-component',
  templateUrl: './signin.component.html',
  styleUrls: ["./signin.component.scss"],
  standalone: false
})

export class SignInComponent implements OnInit {
  loginForm!: FormGroup;
  changePasswordForm!: FormGroup;
  authFailed: boolean = false;
  errorMessage: string = '';
  signedIn: boolean = false;
  passwordExpired: boolean = false;
  changingPassword: boolean = false;
  changePasswordError: string = '';

  private expiredEmail: string = '';
  private expiredPassword: string = '';

  @Input()
  returnUrl: string | string[] = "";

  constructor(
    private authService: IdentityAuthService,
    private userService: UserService,
    private identityClient: IdentityClient,
    private formBuilder: FormBuilder,
    private router: Router
  ) {
    this.authService.onStateChanged().subscribe(async (state: AuthState) => {
      this.signedIn = state == AuthState.LoggedIn;

      if (state === AuthState.LoggedIn) {
        let token = await this.authService.getToken();
        this.handleRedirect(token!);
      }
    });
  }

  ngOnInit(): void {
    this.authFailed = false;
    this.errorMessage = '';
    this.loginForm = this.formBuilder.group(
      {
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required]]
      });
    this.changePasswordForm = this.formBuilder.group(
      {
        newPassword: ['', [Validators.required, Validators.minLength(6)]],
        confirmPassword: ['', [Validators.required]]
      },
      { validators: this.passwordsMatchValidator }
    );
  }

  passwordsMatchValidator(group: AbstractControl): ValidationErrors | null {
    const newPassword = group.get('newPassword')?.value;
    const confirmPassword = group.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { passwordsMismatch: true };
  }

  handleRedirect(token: AccessTokenResponse): void {
    if (this.userService.isSignedIn) {
      this._handleRedirectInner(token);
    } else {
      this.userService.UserSubject.pipe(first()).subscribe(user => {
        this._handleRedirectInner(token);
      });
    }
  }

  private _handleRedirectInner(token: AccessTokenResponse) {
    if (window.opener) {
      window.opener.postMessage({ type: "auth", token: token }, "*");
      window.close();
    }
    else if (this.returnUrl instanceof Array) {
      window.location.href = this.returnUrl[0];
    }
    else if (this.returnUrl.startsWith("http:") || this.returnUrl.startsWith("https:")) {
      window.location.href = this.returnUrl;
    } else if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      this.router.navigateByUrl("home");
    }
  }

  public signIn(_: any) {
    if (!this.loginForm.valid) {
      return;
    }
    const email = this.loginForm.get('email')?.value;
    const password = this.loginForm.get('password')?.value;
    this.authService.signIn(email, password).forEach(
      response => {
        if (response) {
          this.handleRedirect(response);
        }
      }).catch(
        (err: unknown) => {
          this.authFailed = true;
          let detail = '';
          if (err instanceof ApiException) {
            try { detail = JSON.parse(err.response)?.detail ?? ''; } catch { }
          }
          if (detail === 'UserDisabled') {
            this.errorMessage = 'Your account has been disabled. Please contact your administrator.';
          } else if (detail === 'PasswordExpired') {
            this.expiredEmail = email;
            this.expiredPassword = password;
            this.passwordExpired = true;
            this.authFailed = false;
            this.errorMessage = '';
          } else {
            this.errorMessage = 'Invalid credentials';
          }
        });
  }

  public submitNewPassword() {
    if (!this.changePasswordForm.valid) {
      return;
    }
    this.changingPassword = true;
    this.changePasswordError = '';

    const newPassword = this.changePasswordForm.get('newPassword')?.value;
    this.identityClient.changeExpiredPassword(new ChangeExpiredPasswordRequest({
      Email: this.expiredEmail,
      OldPassword: this.expiredPassword,
      NewPassword: newPassword
    })).subscribe({
      next: (response) => {
        this.changingPassword = false;
        this.authService.setToken(response);
        this.handleRedirect(response);
      },
      error: (err: unknown) => {
        this.changingPassword = false;
        if (err instanceof ApiException) {
          try {
            const body = JSON.parse(err.response);
            if (body.errors) {
              const messages = Object.values(body.errors).flat();
              this.changePasswordError = (messages as string[]).join(' ');
            } else if (body.detail) {
              this.changePasswordError = body.detail;
            } else {
              this.changePasswordError = 'Failed to change password. Please try again.';
            }
          } catch {
            this.changePasswordError = 'Failed to change password. Please try again.';
          }
        } else {
          this.changePasswordError = 'Failed to change password. Please try again.';
        }
      }
    });
  }

  public backToSignIn() {
    this.passwordExpired = false;
    this.changePasswordError = '';
    this.changePasswordForm.reset();
    this.expiredEmail = '';
    this.expiredPassword = '';
  }
}
